import { useState } from 'react';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useAuth } from '@/hooks/useAuth';
import { useAccessResolver } from '@/contexts/AccessResolverContext';
import { useModuleAccess } from '@/hooks/useModuleAccess';
import { useWorkspaceUsers, WorkspaceUser, InviteUserRequest } from '@/hooks/useWorkspaceUsers';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Users, Plus, Mail, Shield, UserX, UserCheck, Clock, AlertCircle, Send } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

export default function Users() {
  const { currentWorkspace, workspaceRole, isAdmin: isWorkspaceAdmin, isOwner: isWorkspaceOwner } = useWorkspace();
  const { user: currentUser } = useAuth();
  const { toast } = useToast();
  const { isAdmin } = useAccessResolver();
  const moduleAccess = useModuleAccess('workspace', currentWorkspace?.id || '');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteFirstName, setInviteFirstName] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'member' | 'viewer'>('member');
  const [editingUser, setEditingUser] = useState<WorkspaceUser | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);

  const {
    users,
    isLoading,
    error,
    inviteUser,
    updateUser,
    removeUser,
    resendInvitation,
    isInviting,
    isUpdating,
    isRemoving,
    isResendingInvitation,
  } = useWorkspaceUsers(currentWorkspace?.id);

  // Check if user can access this page
  // Visible ONLY if workspace role is owner OR admin (or super_admin)
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const canAccess = isSuperAdmin || 
                   isWorkspaceOwner || 
                   isWorkspaceAdmin ||
                   workspaceRole === 'owner' || 
                   workspaceRole === 'admin';

  // Check if user can invite (same as canAccess)
  const canInvite = canAccess;

  // If user doesn't have access, show access denied
  // Check this AFTER hooks are called
  if (!canAccess) {
    return (
      <div className="space-y-6 p-6">
        <Alert variant="destructive">
          <Shield className="h-4 w-4" />
          <AlertDescription>
            You do not have permission to access user management. Only workspace owners and administrators can manage users.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Show loading if workspace is not available yet
  if (!currentWorkspace?.id) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-96" />
          </div>
          <Skeleton className="h-10 w-32" />
        </div>
      </div>
    );
  }

  const handleInvite = async () => {
    // Validate required fields
    if (!inviteFirstName.trim()) {
      setInviteError('First name is required');
      toast({
        title: 'Error',
        description: 'First name is required',
        variant: 'destructive',
      });
      return;
    }

    if (!inviteEmail.trim()) {
      setInviteError('Email is required');
      toast({
        title: 'Error',
        description: 'Email is required',
        variant: 'destructive',
      });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteEmail.trim())) {
      setInviteError('Please enter a valid email address');
      toast({
        title: 'Error',
        description: 'Please enter a valid email address',
        variant: 'destructive',
      });
      return;
    }

    setInviteError(null);

    try {
      await inviteUser({
        email: inviteEmail.trim(),
        first_name: inviteFirstName.trim(),
        role: inviteRole,
      });
      
      // Success toast with first name
      toast({
        title: 'Success',
        description: `Invitation sent to ${inviteFirstName.trim()}`,
      });
      
      setShowInviteModal(false);
      setInviteEmail('');
      setInviteFirstName('');
      setInviteRole('member');
      setInviteError(null);
    } catch (err: any) {
      const errorMessage = err.message || err.response?.data?.message || 'Failed to send invitation';
      setInviteError(errorMessage);
      
      // Check for duplicate invite
      if (errorMessage.toLowerCase().includes('already') || 
          errorMessage.toLowerCase().includes('duplicate') ||
          err.response?.status === 409) {
        toast({
          title: 'User Already Invited',
          description: 'This user is already a member or has a pending invitation',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Error',
          description: errorMessage,
          variant: 'destructive',
        });
      }
    }
  };

  const handleUpdateRole = async (userId: string, newRole: string) => {
    const userToUpdate = users.find(u => u.id === userId);
    
    // Prevent owner from demoting self
    if (userToUpdate?.user_id === currentUser?.id && userToUpdate?.role === 'owner' && newRole !== 'owner') {
      toast({
        title: 'Error',
        description: 'You cannot change your own role from owner',
        variant: 'destructive',
      });
      setEditingUser(null);
      return;
    }

    try {
      await updateUser({
        userId,
        data: { role: newRole as any },
      });
      toast({
        title: 'Success',
        description: 'User role updated successfully',
      });
      setEditingUser(null);
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.response?.data?.message || 'Failed to update user role',
        variant: 'destructive',
      });
    }
  };

  const handleToggleStatus = async (user: WorkspaceUser) => {
    // Prevent owner from disabling self
    if (user.user_id === currentUser?.id && user.role === 'owner') {
      toast({
        title: 'Error',
        description: 'You cannot disable your own account',
        variant: 'destructive',
      });
      return;
    }

    const newStatus = user.status === 'active' ? 'suspended' : 'active';

    try {
      await updateUser({
        userId: user.id,
        data: { status: newStatus },
      });
      toast({
        title: 'Success',
        description: `User ${newStatus === 'active' ? 'activated' : 'suspended'} successfully`,
      });
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.response?.data?.message || 'Failed to update user status',
        variant: 'destructive',
      });
    }
  };

  const handleRemove = async (user: WorkspaceUser) => {
    // Prevent owner from removing self
    if (user.user_id === currentUser?.id) {
      toast({
        title: 'Error',
        description: 'You cannot remove your own account',
        variant: 'destructive',
      });
      return;
    }

    if (!confirm(`Are you sure you want to remove ${user.email} from this workspace?`)) {
      return;
    }

    try {
      await removeUser(user.id);
      toast({
        title: 'Success',
        description: 'User removed successfully',
      });
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.response?.data?.message || 'Failed to remove user',
        variant: 'destructive',
      });
    }
  };

  const handleResendInvitation = async (user: WorkspaceUser) => {
    if (!user.invitation_id) {
      toast({
        title: 'Error',
        description: 'Invitation ID not found. Cannot resend invitation.',
        variant: 'destructive',
      });
      return;
    }

    try {
      await resendInvitation(user.invitation_id);
      toast({
        title: 'Success',
        description: 'Invitation resent successfully',
      });
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.response?.data?.message || 'Failed to resend invitation',
        variant: 'destructive',
      });
    }
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'owner':
        return 'default';
      case 'admin':
        return 'secondary';
      case 'member':
        return 'outline';
      case 'viewer':
        return 'outline';
      default:
        return 'outline';
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'active':
        return 'default';
      case 'invited':
        return 'secondary';
      case 'suspended':
        return 'destructive';
      case 'disabled':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'invited':
        return <Clock className="h-3 w-3" />;
      case 'suspended':
      case 'disabled':
        return <AlertCircle className="h-3 w-3" />;
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-96" />
          </div>
          <Skeleton className="h-10 w-32" />
        </div>
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Alert variant="destructive">
          <AlertDescription>
            Failed to load users. Please try again later.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <h1 className="text-3xl font-bold">User Management</h1>
          <p className="text-muted-foreground">
            Manage workspace members and their roles
          </p>
        </div>
        {canInvite && (
          <Button onClick={() => setShowInviteModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Invite User
          </Button>
        )}
      </div>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Workspace Members
          </CardTitle>
          <CardDescription>
            {users.length} {users.length === 1 ? 'member' : 'members'} in this workspace
          </CardDescription>
        </CardHeader>
        <CardContent>
          {users.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="h-16 w-16 mx-auto mb-4 opacity-30" />
              <p className="text-lg font-medium mb-2">No users found</p>
              <p className="text-sm mb-4">Invite users to collaborate on this workspace</p>
              {canInvite && (
                <Button onClick={() => setShowInviteModal(true)} variant="outline">
                  <Plus className="h-4 w-4 mr-2" />
                  Invite First User
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => {
                  const isCurrentUser = user.user_id === currentUser?.id;
                  const isOwner = user.role === 'owner';
                  const canEdit = !isCurrentUser || !isOwner;

                  return (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {user.email}
                          {isCurrentUser && (
                            <Badge variant="outline" className="text-xs">You</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {editingUser?.id === user.id ? (
                          <Select
                            value={user.role}
                            onValueChange={(value) => handleUpdateRole(user.id, value)}
                            disabled={!canEdit || isUpdating}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="owner">Owner</SelectItem>
                              <SelectItem value="admin">Admin</SelectItem>
                              <SelectItem value="member">Member</SelectItem>
                              <SelectItem value="viewer">Viewer</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <Badge variant={getRoleBadgeVariant(user.role)}>
                            {user.role}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <Badge 
                            variant={getStatusBadgeVariant(user.status)}
                            className="flex items-center gap-1.5 w-fit"
                          >
                            {getStatusIcon(user.status)}
                            <span className="capitalize">
                              {user.status === 'invited' ? 'Invited' : user.status === 'active' ? 'Active' : user.status}
                            </span>
                          </Badge>
                          {user.status === 'invited' && user.invitation_expires_at && (
                            <div className="text-xs text-muted-foreground">
                              Expires: {new Date(user.invitation_expires_at).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {user.status === 'invited' 
                          ? 'Pending' 
                          : new Date(user.joined_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {editingUser?.id === user.id ? (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setEditingUser(null)}
                                disabled={isUpdating}
                              >
                                Cancel
                              </Button>
                            </>
                          ) : (
                            <>
                              {/* Resend Invite button - only for invited users */}
                              {user.status === 'invited' && user.invitation_id && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleResendInvitation(user)}
                                  disabled={isResendingInvitation || !canAccess}
                                  title={!canAccess ? 'Only admins can resend invitations' : 'Resend invitation'}
                                >
                                  <Send className="h-4 w-4" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setEditingUser(user)}
                                disabled={!canEdit}
                                title={!canEdit ? 'Cannot edit your own owner role' : 'Edit role'}
                              >
                                <Shield className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleToggleStatus(user)}
                                disabled={isCurrentUser && isOwner}
                                title={isCurrentUser && isOwner ? 'Cannot disable your own account' : 'Toggle status'}
                              >
                                {user.status === 'active' ? (
                                  <UserX className="h-4 w-4" />
                                ) : (
                                  <UserCheck className="h-4 w-4" />
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemove(user)}
                                disabled={isCurrentUser}
                                title={isCurrentUser ? 'Cannot remove your own account' : 'Remove user'}
                              >
                                <UserX className="h-4 w-4 text-destructive" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Invite User Modal */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite User</DialogTitle>
            <DialogDescription>
              Send an invitation to join this workspace
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="first_name">First Name</Label>
              <Input
                id="first_name"
                type="text"
                placeholder="John"
                value={inviteFirstName}
                onChange={(e) => {
                  setInviteFirstName(e.target.value);
                  setInviteError(null);
                }}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="user@example.com"
                value={inviteEmail}
                onChange={(e) => {
                  setInviteEmail(e.target.value);
                  setInviteError(null); // Clear error on input change
                }}
                className={inviteError ? 'border-destructive' : ''}
              />
              {inviteError && (
                <p className="text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {inviteError}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select value={inviteRole} onValueChange={(value: any) => setInviteRole(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="viewer">Viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleInvite} disabled={isInviting || !inviteEmail.trim() || !inviteFirstName.trim()}>
              {isInviting ? (
                <>
                  <Mail className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4 mr-2" />
                  Send Invitation
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

